import { VersionStatus } from '@risksmart-app/domain/src/types/consts';
import type { DocumentByIdResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetDocumentByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetDocumentByIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

type UseGetDocumentByIdArgs = {
  documentId: string;
};

const DRAFT_VERSION_STATUSES: VersionStatus[] = [
  VersionStatus.Draft,
  VersionStatus.PendingApproval,
];

const PUBLISHED_VERSION_STATUSES: VersionStatus[] = [
  VersionStatus.Published,
  VersionStatus.Archived,
];

/** The `documentFiles` fields the two version legs need. */
export interface DocumentFileVersion {
  Status: VersionStatus;
  CreatedAtTimestamp: string;
}

/**
 * COI-789 — the two aliased `documentFiles` legs the GraphQL original selects.
 *
 * V2 wrote them as two sub-selects on the same relation, each
 * `order_by: { CreatedAtTimestamp: desc }, limit: 1` over its own status set:
 * `latestDraftVersion` (`draft`, `pending_approval`) and
 * `latestPublishedVersion` (`published`, `archived`). A Drizzle query config
 * cannot express two differently-filtered projections of one relation, so the
 * config loads the whole set and the split happens here.
 *
 * All three arguments are load-bearing, because both consumers index `[0]`
 * (`pages/policy/update/tabs/update/Tab.tsx`,
 * `pages/policy/useLabelledFields.tsx`): the status set decides whether a
 * version is found at all, and the order decides *which* one. Before COI-789
 * this filtered on `Draft` / `Published` alone and applied neither the order nor
 * the limit — so a policy whose only in-flight draft was `pending_approval`
 * showed no draft version, and `LastPublishedDate` came from an arbitrary file.
 */
export const splitDocumentFileVersions = <T extends DocumentFileVersion>(
  files: T[] | undefined
): { latestDraftVersion: T[]; latestPublishedVersion: T[] } => {
  // `CreatedAtTimestamp` is NOT NULL on `document_file`, so a plain descending
  // compare reproduces V2's `desc` with no nulls-ordering question to answer.
  //
  // Compared as [milliseconds, sub-millisecond microseconds] rather than a single
  // `getTime()`. Postgres timestamps carry microsecond precision and `getTime()`
  // truncates to milliseconds, so two versions written inside the same millisecond
  // — a batch insert, or any fast writer — compared EQUAL and the winner fell out
  // as whichever the array happened to hold first. That is the arbitrary pick this
  // whole function exists to remove. Raised by review on #8486.
  //
  // The millisecond half still goes through `Date` so an offset other than `Z` is
  // handled; only the sub-millisecond digits are read off the string, which is
  // where `Date` has already thrown the precision away.
  const createdAtKey = (file: T): [number, number] => {
    const value = file.CreatedAtTimestamp;
    const fraction = /\.(\d+)/.exec(value)?.[1] ?? '';

    return [
      new Date(value).getTime(),
      Number(fraction.padEnd(6, '0').slice(3, 6)),
    ];
  };

  const isNewer = (candidate: T, incumbent: T): boolean => {
    const [candidateMs, candidateMicros] = createdAtKey(candidate);
    const [incumbentMs, incumbentMicros] = createdAtKey(incumbent);

    return candidateMs === incumbentMs
      ? candidateMicros > incumbentMicros
      : candidateMs > incumbentMs;
  };

  const latestWithStatus = (statuses: VersionStatus[]): T[] => {
    let latest: T | undefined;
    for (const file of files ?? []) {
      if (!statuses.includes(file.Status)) {
        continue;
      }
      if (!latest || isNewer(file, latest)) {
        latest = file;
      }
    }

    return latest ? [latest] : [];
  };

  return {
    latestDraftVersion: latestWithStatus(DRAFT_VERSION_STATUSES),
    latestPublishedVersion: latestWithStatus(PUBLISHED_VERSION_STATUSES),
  };
};

export function mapTrpcDocumentToGraphQL(
  document: DocumentByIdResponseRow
): StrictMappedGraphQLSelection<GetDocumentByIdQuery['document'][number]> {
  return {
    ...document,
    ...splitDocumentFileVersions(document.documentFiles),
  };
}

export const useGetDocumentById = createQueryHook<
  UseGetDocumentByIdArgs,
  DocumentByIdResponseRow[],
  GetDocumentByIdQuery
>({
  trpcQueryOptions: (trpc, { documentId }) =>
    trpc.frontend.document.documentById.queryOptions({ documentId }),
  mapTrpcDataToGraphQL: (data) => ({
    document: data.map(mapTrpcDocumentToGraphQL),
  }),
  graphqlDocument: GetDocumentByIdDocument,
  graphqlVariables: ({ documentId }) => ({ id: documentId }),
});
